/**
 * useConfigBridge
 *
 * Subscribes to sceneStore changes and automatically writes the
 * slot game config to `slot-core/game.config.json` when the scene
 * contains a slot game and slot-core is installed.
 */

import { useEffect, useRef } from 'react';
import { useSceneStore } from '@store/sceneStore';
import { scheduleConfigWrite } from '@renderer/services/slot-core/ConfigBridgeService';
import { isSlotCoreInstalled } from '@renderer/services/slot-core';
import type { GameObject } from '@core/types/gameobject.types';

/**
 * Returns true if any GameObject in the record is a slot game root.
 */
function hasSlotRoot(gameObjects: Record<string, GameObject>): boolean {
  return Object.values(gameObjects).some(
    go => go.type === 'cascade-slot' || go.type === 'backend-slot-game'
  );
}

/**
 * Hook that watches sceneStore.gameObjects and triggers a debounced
 * config write whenever the scene contains a slot game.
 *
 * The hook is inert (no subscriptions, no writes) when:
 * - slot-core is not installed in the project
 * - the scene has no cascade-slot or backend-slot-game root
 */
export function useConfigBridge(): void {
  const slotCoreInstalled = useRef(false);
  const checkedOnce = useRef(false);

  // Check slot-core installation status on mount and when install events fire
  useEffect(() => {
    const check = async () => {
      slotCoreInstalled.current = await isSlotCoreInstalled();
      checkedOnce.current = true;
    };

    check();

    // Re-check when slot-core is freshly installed
    const onInstallComplete = () => {
      check();
    };
    window.addEventListener('slot-core:install-complete', onInstallComplete);

    return () => {
      window.removeEventListener('slot-core:install-complete', onInstallComplete);
    };
  }, []);

  // Subscribe to gameObjects changes in sceneStore
  useEffect(() => {
    const unsubscribe = useSceneStore.subscribe(
      (state) => {
        // Only react when slot-core is confirmed installed
        if (!checkedOnce.current || !slotCoreInstalled.current) return;

        // Only write if there's a slot game in the scene
        if (!hasSlotRoot(state.gameObjects)) return;

        scheduleConfigWrite();
      }
    );

    return unsubscribe;
  }, []);
}
